import { useEffect, useMemo, useRef, useState } from 'react';
import * as maptilersdk from '@maptiler/sdk';
import '@maptiler/sdk/dist/maptiler-sdk.css';
import { ColorRamp, PrecipitationLayer, RadarLayer, TemperatureLayer, WindLayer, type TileLayer } from '@maptiler/weather';
import {
  fetchPowerPlantMapPreferences,
  savePowerPlantMapPreferences,
  type PowerPlant,
  type PowerPlantMapPreferences,
} from '@/api/powerplants';
import type { WeatherData } from '@/api/weather';
import { useI18n } from '@/i18n';

type WorldPlantMapProps = {
  plants: PowerPlant[];
  weatherByPlantUuid?: Record<string, WeatherData | undefined>;
  onPlantSelect?: (uuidId: string) => void;
};

type BasemapOption = {
  id: string;
  label: string;
};

const DEFAULT_CENTER: [number, number] = [35, 39];
const DEFAULT_ZOOM = 5.6;
const MIN_ZOOM = 2.2;
const TURKEY_BOUNDS: maptilersdk.LngLatBoundsLike = [
  [25.5, 35.4],
  [45.2, 42.8],
];
const MAPTILER_API_KEY = import.meta.env.VITE_MAPTILER_API_KEY || 'NqZqG8Ib0S8N4aEUpaZT';
const SOURCE_ID = 'plants-source';
const CLUSTER_LAYER_ID = 'plants-clusters';
const CLUSTER_COUNT_LAYER_ID = 'plants-cluster-count';
const POINT_LAYER_ID = 'plants-points';
const POINT_LABEL_LAYER_ID = 'plants-point-labels';
const BASEMAP_OPTIONS: BasemapOption[] = [
  { id: 'streets-v2', label: 'Streets' },
  { id: 'basic-v2', label: 'Basic' },
  { id: 'dataviz-light', label: 'Dataviz Light' },
  { id: 'satellite', label: 'Satellite' },
];

type NormalizedPlant = PowerPlant & {
  lat: number;
  lon: number;
};

type ClusterLeafProperties = {
  uuid_id: string;
  name: string;
  day_power: string;
  day_power_value: number;
  weather: string;
};

type HoverPlantOption = {
  uuid_id: string;
  name: string;
  day_power: string;
  weather: string;
};

type HoverMenuState = {
  x: number;
  y: number;
  items: HoverPlantOption[];
} | null;

type MapViewState = {
  center: [number, number];
  zoom: number;
  bearing: number;
  pitch: number;
};

type WeatherOverlayOption = 'none' | 'temperature' | 'precipitation' | 'wind' | 'radar' | 'clouds';

const WEATHER_LAYER_IDS = [
  'MapTiler Temperature',
  'MapTiler Precipitation',
  'MapTiler Wind',
  'MapTiler Radar',
  'MapTiler Clouds',
] as const;

function normalizeCoordinate(value: unknown): number | null {
  if (value === null || value === undefined) return null;
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value === 'string') {
    const normalizedText = value.trim().replace(',', '.');
    if (!normalizedText) return null;
    const parsed = Number(normalizedText);
    return Number.isFinite(parsed) ? parsed : null;
  }
  const normalized = Number(value);
  return Number.isFinite(normalized) ? normalized : null;
}

function normalizePlantCoordinates(latValue: number | null, lonValue: number | null): { lat: number; lon: number } | null {
  const lat = normalizeCoordinate(latValue);
  const lon = normalizeCoordinate(lonValue);

  if (lat === null || lon === null) return null;

  const looksLikeTurkeyLat = lat >= 35 && lat <= 43.5;
  const looksLikeTurkeyLon = lon >= 25 && lon <= 45.5;
  if (looksLikeTurkeyLat && looksLikeTurkeyLon) {
    return { lat, lon };
  }

  const swappedLooksLikeTurkeyLat = lon >= 35 && lon <= 43.5;
  const swappedLooksLikeTurkeyLon = lat >= 25 && lat <= 45.5;
  if (swappedLooksLikeTurkeyLat && swappedLooksLikeTurkeyLon) {
    return { lat: lon, lon: lat };
  }

  return { lat, lon };
}

function buildFeatureCollection(
  plants: NormalizedPlant[],
  weatherByPlantUuid: Record<string, WeatherData | undefined>,
) {
  function parseDayPowerValue(value: string): number {
    const unitText = value.toLowerCase();
    const normalized = value
      .replace(/\s/g, '')
      .replace(/[A-Za-zÇĞİÖŞÜçğıöşü]+/g, '')
      .replace(/\.(?=\d{3}(?:,|$))/g, '')
      .replace(',', '.');
    const parsed = Number(normalized);
    if (!Number.isFinite(parsed)) return 0;

    if (unitText.includes('gwh')) return parsed * 1_000_000;
    if (unitText.includes('mwh')) return parsed * 1_000;
    return parsed;
  }

  function weatherEmoji(weather?: WeatherData): string {
    const icon = weather?.currentConditions.icon?.toLowerCase() ?? '';
    if (icon.includes('rain') || icon.includes('showers')) return '🌧';
    if (icon.includes('snow')) return '❄️';
    if (icon.includes('fog') || icon.includes('mist')) return '🌫';
    if (icon.includes('wind')) return '💨';
    if (icon.includes('thunder') || icon.includes('storm')) return '⛈';
    if (icon.includes('cloud')) return '☁️';
    if (icon.includes('clear') || icon.includes('sun')) return '☀️';
    return '⛅';
  }

  return {
    type: 'FeatureCollection' as const,
    features: plants.map((plant) => ({
      type: 'Feature' as const,
      geometry: {
        type: 'Point' as const,
        coordinates: [plant.lon, plant.lat] as [number, number],
      },
      properties: {
        uuid_id: plant.power_plant_uuid_id,
        status: plant.power_plant_status,
        name: plant.power_plant_name,
        day_power: plant.power_plant_e_day,
        day_power_value: parseDayPowerValue(plant.power_plant_e_day),
        weather: weatherByPlantUuid[plant.power_plant_uuid_id]
          ? `${weatherEmoji(weatherByPlantUuid[plant.power_plant_uuid_id])} ${Math.round(weatherByPlantUuid[plant.power_plant_uuid_id]!.currentConditions.temp)}°C`
          : '⛅ -',
      },
    })),
  };
}

function formatClusterProduction(value: number): string {
  const absValue = Math.abs(value);
  if (absValue >= 1_000_000) {
    return `${new Intl.NumberFormat('tr-TR', { maximumFractionDigits: 2 }).format(value / 1_000_000)} GWh`;
  }
  if (absValue >= 1_000) {
    return `${new Intl.NumberFormat('tr-TR', { maximumFractionDigits: 1 }).format(value / 1_000)} MWh`;
  }
  return `${new Intl.NumberFormat('tr-TR', { maximumFractionDigits: 0 }).format(value)} kWh`;
}

export default function WorldPlantMap({
  plants,
  weatherByPlantUuid = {},
  onPlantSelect,
}: WorldPlantMapProps) {
  const { t } = useI18n();
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<maptilersdk.Map | null>(null);
  const viewStateRef = useRef<MapViewState | null>(null);
  const clusterLabelModeRef = useRef<'hidden' | 'visible'>('hidden');
  const weatherOverlayRef = useRef<WeatherOverlayOption>('none');
  const updateClusterMarkersRef = useRef<(() => Promise<void>) | null>(null);
  const syncWeatherLayerRef = useRef<(() => void) | null>(null);
  const clearTemperatureGridMarkersRef = useRef<(() => void) | null>(null);
  const validPlantsRef = useRef<NormalizedPlant[]>([]);
  const weatherByPlantUuidRef = useRef<Record<string, WeatherData | undefined>>({});
  const clusterMarkersRef = useRef<maptilersdk.Marker[]>([]);
  const temperatureGridMarkersRef = useRef<maptilersdk.Marker[]>([]);
  const weatherLayerRef = useRef<TileLayer | null>(null);
  const hoverHideTimeoutRef = useRef<number | null>(null);
  const [activeUuid, setActiveUuid] = useState<string | null>(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [selectedBasemap, setSelectedBasemap] = useState<string>('streets-v2');
  const [clusterLabelMode, setClusterLabelMode] = useState<'hidden' | 'visible'>('hidden');
  const [weatherOverlay, setWeatherOverlay] = useState<WeatherOverlayOption>('none');
  const [hoverMenu, setHoverMenu] = useState<HoverMenuState>(null);
  const [preferencesLoaded, setPreferencesLoaded] = useState(false);

  const validPlants = useMemo(
    () =>
      plants
        .map((plant) => {
          const normalizedCoordinates = normalizePlantCoordinates(plant.power_plant_lat, plant.power_plant_lon);
          if (!normalizedCoordinates) return null;
          return { ...plant, ...normalizedCoordinates };
        })
        .filter((plant): plant is NonNullable<typeof plant> => plant !== null),
    [plants],
  );

  const activePlant =
    validPlants.find((plant) => plant.power_plant_uuid_id === activeUuid) ??
    validPlants[0] ??
    null;

  useEffect(() => {
    validPlantsRef.current = validPlants;
  }, [validPlants]);

  useEffect(() => {
    weatherByPlantUuidRef.current = weatherByPlantUuid;
  }, [weatherByPlantUuid]);

  useEffect(() => {
    let cancelled = false;

    fetchPowerPlantMapPreferences()
      .then((preferences) => {
        if (cancelled) return;
        const nextBasemap =
          preferences.basemap && BASEMAP_OPTIONS.some((option) => option.id === preferences.basemap)
            ? preferences.basemap
            : 'streets-v2';
        setSelectedBasemap(nextBasemap);
        setClusterLabelMode(preferences.clusterLabelMode === 'visible' ? 'visible' : 'hidden');
        setWeatherOverlay(preferences.weatherOverlay ?? 'none');
        setActiveUuid(preferences.activeUuid ?? null);
      })
      .catch(() => {
        if (!cancelled) {
          setSelectedBasemap('streets-v2');
          setClusterLabelMode('hidden');
          setWeatherOverlay('none');
        }
      })
      .finally(() => {
        if (!cancelled) setPreferencesLoaded(true);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!preferencesLoaded) return;

    const payload: PowerPlantMapPreferences = {
      basemap: selectedBasemap,
      clusterLabelMode,
      weatherOverlay,
      activeUuid,
    };

    void savePowerPlantMapPreferences(payload).catch(() => {
      // Ignore persistence failures and keep UI responsive.
    });
  }, [preferencesLoaded, selectedBasemap, clusterLabelMode, weatherOverlay, activeUuid]);

  useEffect(() => {
    clusterLabelModeRef.current = clusterLabelMode;
  }, [clusterLabelMode]);

  useEffect(() => {
    weatherOverlayRef.current = weatherOverlay;
  }, [weatherOverlay]);

  function getStyleUrl(styleId: string) {
    return `https://api.maptiler.com/maps/${styleId}/style.json?key=${MAPTILER_API_KEY}`;
  }

  function cancelHoverHide() {
    if (hoverHideTimeoutRef.current !== null) {
      window.clearTimeout(hoverHideTimeoutRef.current);
      hoverHideTimeoutRef.current = null;
    }
  }

  function scheduleHoverHide() {
    cancelHoverHide();
    hoverHideTimeoutRef.current = window.setTimeout(() => {
      setHoverMenu(null);
      hoverHideTimeoutRef.current = null;
    }, 140);
  }

  useEffect(() => {
    if (!mapContainerRef.current) return;

    if (mapRef.current) {
      viewStateRef.current = {
        center: [mapRef.current.getCenter().lng, mapRef.current.getCenter().lat],
        zoom: mapRef.current.getZoom(),
        bearing: mapRef.current.getBearing(),
        pitch: mapRef.current.getPitch(),
      };
      clusterMarkersRef.current.forEach((marker) => marker.remove());
      clusterMarkersRef.current = [];
      mapRef.current.remove();
      mapRef.current = null;
      setMapLoaded(false);
    }

    maptilersdk.config.apiKey = MAPTILER_API_KEY;

    const initialViewState = viewStateRef.current;
    const map = new maptilersdk.Map({
      container: mapContainerRef.current,
      style: getStyleUrl(selectedBasemap),
      center: initialViewState?.center ?? DEFAULT_CENTER,
      zoom: initialViewState?.zoom ?? DEFAULT_ZOOM,
      bearing: initialViewState?.bearing ?? 0,
      pitch: initialViewState?.pitch ?? 0,
      minZoom: MIN_ZOOM,
      maxZoom: 16,
    });

    map.addControl(new maptilersdk.NavigationControl({ visualizePitch: true }), 'top-right');
    mapRef.current = map;

    const bindLayerEvents = () => {
      const showHoverMenu = (x: number, y: number, items: HoverPlantOption[]) => {
        cancelHoverHide();
        if (items.length <= 1) {
          setHoverMenu(null);
          return;
        }
        setHoverMenu({ x, y, items });
      };

      map.on('click', CLUSTER_LAYER_ID, async (event) => {
        const feature = event.features?.[0];
        if (!feature) return;
        const clusterId = feature.properties?.cluster_id;
        const source = map.getSource(SOURCE_ID) as maptilersdk.GeoJSONSource | undefined;
        if (!source || clusterId === undefined) return;
        const zoom = await source.getClusterExpansionZoom(Number(clusterId));
        const coordinates = (feature.geometry as GeoJSON.Point).coordinates as [number, number];
        map.easeTo({
          center: coordinates,
          zoom,
          duration: 500,
        });
      });

      map.on('click', POINT_LAYER_ID, (event) => {
        const rendered = map
          .queryRenderedFeatures(event.point, { layers: [POINT_LAYER_ID] })
          .filter((feature) => typeof feature.properties?.uuid_id === 'string');

        const uniqueItems = Array.from(
          new Map(
            rendered.map((feature) => [
              String(feature.properties!.uuid_id),
              {
                uuid_id: String(feature.properties!.uuid_id),
                name: String(feature.properties!.name ?? ''),
                day_power: String(feature.properties!.day_power ?? ''),
                weather: String(feature.properties!.weather ?? ''),
              },
            ]),
          ).values(),
        );

        if (uniqueItems.length > 1) {
          showHoverMenu(event.point.x, event.point.y, uniqueItems);
          return;
        }

        const feature = rendered[0] ?? event.features?.[0];
        const uuidId = feature?.properties?.uuid_id;
        if (typeof uuidId === 'string') {
          setHoverMenu(null);
          setActiveUuid(uuidId);
        }
      });

      map.on('mouseenter', CLUSTER_LAYER_ID, () => {
        map.getCanvas().style.cursor = 'pointer';
      });
      map.on('mousemove', CLUSTER_LAYER_ID, async (event) => {
        const feature = event.features?.[0];
        const clusterId = feature?.properties?.cluster_id;
        const source = map.getSource(SOURCE_ID) as maptilersdk.GeoJSONSource | undefined;
        if (!source || clusterId === undefined) return;
        const leaves = await source.getClusterLeaves(Number(clusterId), 20, 0);
        const items = Array.from(
          new Map(
            leaves.map((leaf) => {
              const properties = leaf.properties as unknown as ClusterLeafProperties;
              return [
                properties.uuid_id,
                {
                  uuid_id: properties.uuid_id,
                  name: properties.name,
                  day_power: properties.day_power,
                  weather: properties.weather,
                },
              ];
            }),
          ).values(),
        );
        showHoverMenu(event.point.x, event.point.y, items);
      });
      map.on('mouseleave', CLUSTER_LAYER_ID, () => {
        map.getCanvas().style.cursor = '';
        scheduleHoverHide();
      });
      map.on('mouseenter', POINT_LAYER_ID, () => {
        map.getCanvas().style.cursor = 'pointer';
      });
      map.on('mousemove', POINT_LAYER_ID, (event) => {
        const rendered = map
          .queryRenderedFeatures(event.point, { layers: [POINT_LAYER_ID] })
          .filter((feature) => typeof feature.properties?.uuid_id === 'string');

        const items = Array.from(
          new Map(
            rendered.map((feature) => [
              String(feature.properties!.uuid_id),
              {
                uuid_id: String(feature.properties!.uuid_id),
                name: String(feature.properties!.name ?? ''),
                day_power: String(feature.properties!.day_power ?? ''),
                weather: String(feature.properties!.weather ?? ''),
              },
            ]),
          ).values(),
        );
        showHoverMenu(event.point.x, event.point.y, items);
      });
      map.on('mouseleave', POINT_LAYER_ID, () => {
        map.getCanvas().style.cursor = '';
        scheduleHoverHide();
      });
    };

    const clearClusterMarkers = () => {
      clusterMarkersRef.current.forEach((marker) => marker.remove());
      clusterMarkersRef.current = [];
    };

    const clearTemperatureGridMarkers = () => {
      temperatureGridMarkersRef.current.forEach((marker) => marker.remove());
      temperatureGridMarkersRef.current = [];
    };

    const updateClusterMarkers = async () => {
      if (!map.isStyleLoaded() || !map.getLayer(CLUSTER_LAYER_ID)) return;

      clearClusterMarkers();
      if (clusterLabelModeRef.current !== 'visible') return;

      const source = map.getSource(SOURCE_ID) as maptilersdk.GeoJSONSource | undefined;
      if (!source) return;

      const clusterFeatures = map.queryRenderedFeatures({ layers: [CLUSTER_LAYER_ID] });
      for (const clusterFeature of clusterFeatures) {
        const clusterId = clusterFeature.properties?.cluster_id;
        const coordinates = (clusterFeature.geometry as GeoJSON.Point).coordinates as [number, number];
        if (clusterId === undefined || !coordinates) continue;

        const leaves = await source.getClusterLeaves(Number(clusterId), 100, 0);
        const leafProps = leaves.map((leaf) => leaf.properties as unknown as ClusterLeafProperties);
        if (leafProps.length === 0) continue;
        const totalProduction = leafProps.reduce((sum, leaf) => sum + (leaf.day_power_value ?? 0), 0);
        const totalProductionLabel = formatClusterProduction(totalProduction);
        const plantCount = leafProps.length;

        const el = document.createElement('div');
        el.className = 'pointer-events-none';
        el.innerHTML = `
          <div style="display:flex;flex-direction:column;gap:1px;min-width:120px;max-width:150px;padding:0 2px;text-align:center;">
            <div style="font-size:11.5px;font-weight:800;line-height:1.15;color:#1d4ed8;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;text-shadow:0 0 2.4px rgba(255,255,255,0.96),0 0 2.4px rgba(255,255,255,0.96),0 0 2.4px rgba(255,255,255,0.96);">Toplam Üretim</div>
            <div style="font-size:11px;font-weight:800;line-height:1.15;color:#1d4ed8;text-shadow:0 0 2.4px rgba(255,255,255,0.96),0 0 2.4px rgba(255,255,255,0.96),0 0 2.4px rgba(255,255,255,0.96);">${totalProductionLabel}</div>
            <div style="font-size:11px;font-weight:800;line-height:1.15;color:#1d4ed8;text-shadow:0 0 2.4px rgba(255,255,255,0.96),0 0 2.4px rgba(255,255,255,0.96),0 0 2.4px rgba(255,255,255,0.96);">${plantCount} santral</div>
          </div>
        `;

        const marker = new maptilersdk.Marker({
          element: el,
          anchor: 'bottom',
          offset: [0, -16],
        })
          .setLngLat(coordinates)
          .addTo(map);

        clusterMarkersRef.current.push(marker);
      }
    };

    const updateTemperatureGridMarkers = async () => {
      clearTemperatureGridMarkers();
    };

    let eventsBound = false;

    const removeWeatherLayer = () => {
      const existingLayer = weatherLayerRef.current;
      for (const layerId of WEATHER_LAYER_IDS) {
        if (map.getLayer(layerId)) {
          map.removeLayer(layerId);
        }
      }
      if (existingLayer) {
        weatherLayerRef.current = null;
      }
    };

    const createWeatherLayer = (): TileLayer | null => {
      switch (weatherOverlayRef.current) {
        case 'temperature':
          return new TemperatureLayer({ opacity: 0.52 });
        case 'precipitation':
          return new PrecipitationLayer({ opacity: 0.58 });
        case 'wind':
          return new WindLayer({ opacity: 0.62 });
        case 'radar':
          return new RadarLayer({ opacity: 0.55 });
        case 'clouds':
          return new RadarLayer({
            id: 'MapTiler Clouds',
            opacity: 0.45,
            colorramp: ColorRamp.builtin.RADAR_CLOUD,
          });
        default:
          return null;
      }
    };

    const syncWeatherLayer = () => {
      removeWeatherLayer();
      const nextWeatherLayer = createWeatherLayer();
      if (!nextWeatherLayer) return;
      map.addLayer(nextWeatherLayer as unknown as maptilersdk.CustomLayerInterface);
      weatherLayerRef.current = nextWeatherLayer;
    };
    syncWeatherLayerRef.current = syncWeatherLayer;
    updateClusterMarkersRef.current = updateClusterMarkers;
    clearTemperatureGridMarkersRef.current = clearTemperatureGridMarkers;

    const ensurePlantLayers = () => {
      if (!map.isStyleLoaded()) return;

      syncWeatherLayer();

      if (!map.getSource(SOURCE_ID)) {
        map.addSource(SOURCE_ID, {
          type: 'geojson',
          data: {
            type: 'FeatureCollection',
            features: [],
          },
          cluster: true,
          clusterRadius: 42,
          clusterMaxZoom: 12,
        });
      }

      if (!map.getLayer(CLUSTER_LAYER_ID)) {
        map.addLayer({
          id: CLUSTER_LAYER_ID,
          type: 'circle',
          source: SOURCE_ID,
          filter: ['has', 'point_count'],
          paint: {
            'circle-color': [
              'step',
              ['get', 'point_count'],
              '#bfdbfe',
              5,
              '#93c5fd',
              15,
              '#60a5fa',
            ],
            'circle-radius': [
              'step',
              ['get', 'point_count'],
              18,
              5,
              24,
              15,
              30,
            ],
            'circle-stroke-color': '#ffffff',
            'circle-stroke-width': 2,
          },
        });
      }

      if (!map.getLayer(CLUSTER_COUNT_LAYER_ID)) {
        map.addLayer({
          id: CLUSTER_COUNT_LAYER_ID,
          type: 'symbol',
          source: SOURCE_ID,
          filter: ['has', 'point_count'],
          layout: {
            'text-field': ['get', 'point_count_abbreviated'],
            'text-size': 12,
            'text-font': ['Open Sans Bold'],
          },
          paint: {
            'text-color': '#0f172a',
          },
        });
      }

      if (!map.getLayer(POINT_LAYER_ID)) {
        map.addLayer({
          id: POINT_LAYER_ID,
          type: 'circle',
          source: SOURCE_ID,
          filter: ['!', ['has', 'point_count']],
          paint: {
            'circle-color': '#dc2626',
            'circle-radius': 8,
            'circle-stroke-color': '#ffffff',
            'circle-stroke-width': 2,
          },
        });
      }

      if (!map.getLayer(POINT_LABEL_LAYER_ID)) {
        map.addLayer({
          id: POINT_LABEL_LAYER_ID,
          type: 'symbol',
          source: SOURCE_ID,
          filter: ['!', ['has', 'point_count']],
          layout: {
            'text-field': ['concat', ['get', 'name'], '\n', ['get', 'day_power'], '\n', ['get', 'weather']],
            'text-size': 11.5,
            'text-font': ['Open Sans Bold'],
            'text-offset': [0, -2.2],
            'text-anchor': 'bottom',
            'text-max-width': 14,
            'text-allow-overlap': false,
            'text-ignore-placement': false,
            'text-line-height': 1.15,
          },
          paint: {
            'text-color': '#1d4ed8',
            'text-halo-color': 'rgba(255,255,255,0.96)',
            'text-halo-width': 2.4,
            'text-halo-blur': 0.8,
          },
        });
      }

      if (!eventsBound) {
        bindLayerEvents();
        eventsBound = true;
      }

      const source = map.getSource(SOURCE_ID) as maptilersdk.GeoJSONSource | undefined;
      if (source) {
        source.setData(buildFeatureCollection(validPlantsRef.current, weatherByPlantUuidRef.current));
      }

      void updateClusterMarkers();
      void updateTemperatureGridMarkers();
      setMapLoaded(true);
    };

    map.once('load', () => {
      ensurePlantLayers();
      window.requestAnimationFrame(() => {
        map.resize();
      });
    });
    map.on('moveend', () => {
      viewStateRef.current = {
        center: [map.getCenter().lng, map.getCenter().lat],
        zoom: map.getZoom(),
        bearing: map.getBearing(),
        pitch: map.getPitch(),
      };
      void updateClusterMarkers();
      void updateTemperatureGridMarkers();
    });
    map.on('zoomend', () => {
      viewStateRef.current = {
        center: [map.getCenter().lng, map.getCenter().lat],
        zoom: map.getZoom(),
        bearing: map.getBearing(),
        pitch: map.getPitch(),
      };
      void updateClusterMarkers();
      void updateTemperatureGridMarkers();
    });

    const handleResize = () => {
      map.resize();
      void updateClusterMarkers();
      void updateTemperatureGridMarkers();
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      cancelHoverHide();
      clearClusterMarkers();
      clearTemperatureGridMarkers();
      updateClusterMarkersRef.current = null;
      syncWeatherLayerRef.current = null;
      clearTemperatureGridMarkersRef.current = null;
      map.remove();
      mapRef.current = null;
      setMapLoaded(false);
    };
  }, [selectedBasemap]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapLoaded || !map.isStyleLoaded()) return;

    const syncSource = () => {
      const source = map.getSource(SOURCE_ID) as maptilersdk.GeoJSONSource | undefined;
      if (!source) return false;
      source.setData(buildFeatureCollection(validPlants, weatherByPlantUuid));
      return true;
    };

    if (!syncSource()) {
      window.setTimeout(() => {
        syncSource();
      }, 0);
      return;
    }

    map.resize();
    if (!viewStateRef.current) {
      map.fitBounds(TURKEY_BOUNDS, {
        padding: { top: 50, right: 50, bottom: 50, left: 50 },
        duration: 0,
        maxZoom: DEFAULT_ZOOM,
      });
      viewStateRef.current = {
        center: [map.getCenter().lng, map.getCenter().lat],
        zoom: map.getZoom(),
        bearing: map.getBearing(),
        pitch: map.getPitch(),
      };
    }
  }, [mapLoaded, validPlants, weatherByPlantUuid]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapLoaded) return;
    syncWeatherLayerRef.current?.();
    void updateClusterMarkersRef.current?.();
    clearTemperatureGridMarkersRef.current?.();
    map.triggerRepaint();
    clusterMarkersRef.current.forEach((marker) => marker.remove());
    clusterMarkersRef.current = [];
    temperatureGridMarkersRef.current.forEach((marker) => marker.remove());
    temperatureGridMarkersRef.current = [];
  }, [clusterLabelMode, mapLoaded, weatherOverlay]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !activePlant || !activeUuid) return;

    map.flyTo({
      center: [activePlant.lon, activePlant.lat],
      zoom: Math.max(map.getZoom(), 7.8),
      duration: 700,
      essential: true,
    });
  }, [activePlant]);

  return (
    <div className="grid h-full gap-4 lg:grid-cols-[minmax(0,1fr)_320px]">
      <div className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-200 px-4 py-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-2 py-1.5">
              <label className="flex items-center gap-2 px-2 text-sm font-medium text-slate-700">
                <input
                  type="radio"
                  name="cluster-label-mode"
                  checked={clusterLabelMode === 'hidden'}
                  onChange={() => setClusterLabelMode('hidden')}
                  className="h-4 w-4 accent-blue-600"
                />
                Cluster metni gizli
              </label>
              <label className="flex items-center gap-2 px-2 text-sm font-medium text-slate-700">
                <input
                  type="radio"
                  name="cluster-label-mode"
                  checked={clusterLabelMode === 'visible'}
                  onChange={() => setClusterLabelMode('visible')}
                  className="h-4 w-4 accent-blue-600"
                />
                Cluster metni görünür
              </label>
            </div>

            <select
              value={selectedBasemap}
              onChange={(event) => setSelectedBasemap(event.target.value)}
              className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm outline-none transition focus:border-blue-400"
            >
              {BASEMAP_OPTIONS.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </select>

            <select
              value={weatherOverlay}
              onChange={(event) => setWeatherOverlay(event.target.value as WeatherOverlayOption)}
              className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm outline-none transition focus:border-blue-400"
            >
              <option value="none">Hava katmanı kapalı</option>
              <option value="temperature">Sıcaklık</option>
              <option value="precipitation">Yağış</option>
              <option value="wind">Rüzgar</option>
              <option value="radar">Radar</option>
              <option value="clouds">Bulut</option>
            </select>
          </div>
        </div>
        <div className="relative h-[calc(100%-65px)] min-h-[420px] w-full bg-slate-100">
          <div ref={mapContainerRef} className="h-full w-full" />
          {hoverMenu ? (
            <div
              className="absolute z-20 w-64 max-w-[calc(100%-24px)] rounded-xl border border-slate-200 bg-white/95 p-1.5 shadow-xl backdrop-blur-sm"
              style={{
                left: Math.min(hoverMenu.x + 10, 24 + (mapContainerRef.current?.clientWidth ?? 0) - 270),
                top: Math.max(12, hoverMenu.y - 8),
              }}
              onMouseEnter={cancelHoverHide}
              onMouseLeave={scheduleHoverHide}
            >
              <div className="px-2 pb-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                Bu noktadaki santraller
              </div>
              <div className="max-h-48 space-y-1 overflow-y-auto">
                {hoverMenu.items.map((item) => (
                  <button
                    key={item.uuid_id}
                    type="button"
                    onClick={() => {
                      setHoverMenu(null);
                      setActiveUuid(item.uuid_id);
                    }}
                    className="w-full rounded-lg border border-transparent px-2 py-1.5 text-left transition hover:border-blue-200 hover:bg-blue-50"
                  >
                    <div className="truncate text-[12px] font-semibold text-slate-800">{item.name}</div>
                    <div className="mt-0.5 text-[11px] text-slate-500">{item.day_power}</div>
                    <div className="mt-0.5 text-[11px] font-medium text-blue-700">{item.weather}</div>
                  </button>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      </div>

      <aside className="overflow-y-auto rounded-[28px] border border-slate-200 bg-white p-5 shadow-sm">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h3 className="text-base font-semibold text-slate-900">{t('home.map.detailTitle')}</h3>
            <p className="mt-1 text-sm text-slate-500">{t('home.map.detailHint')}</p>
          </div>
        </div>

        {activePlant ? (
          <div className="space-y-4">
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                {activePlant.power_plant_investor_name}
              </div>
              <div className="mt-2 text-lg font-semibold text-slate-900">{activePlant.power_plant_name}</div>
              <div className="mt-1 text-sm text-slate-500">
                {activePlant.power_plant_town}, {activePlant.power_plant_city}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-2xl bg-emerald-50 p-4">
                <div className="text-xs font-semibold uppercase tracking-[0.12em] text-emerald-700">{t('common.eDay')}</div>
                <div className="mt-1 text-lg font-semibold text-emerald-900">{activePlant.power_plant_e_day}</div>
              </div>
              <div className="rounded-2xl bg-blue-50 p-4">
                <div className="text-xs font-semibold uppercase tracking-[0.12em] text-blue-700">{t('common.eTotal')}</div>
                <div className="mt-1 text-lg font-semibold text-blue-900">{activePlant.power_plant_e_total}</div>
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 p-4">
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-500">{t('common.lastSeen')}</span>
                <span className="font-medium text-slate-800">{activePlant.power_plant_last_seen || '-'}</span>
              </div>
              <div className="mt-3 flex items-center justify-between text-sm">
                <span className="text-slate-500">{t('common.status')}</span>
                <span className={`font-medium ${activePlant.power_plant_status === 1 ? 'text-emerald-600' : 'text-slate-500'}`}>
                  {activePlant.power_plant_status === 1 ? t('common.online') : t('common.offline')}
                </span>
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 p-4">
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-500">Lat</span>
                <span className="font-medium text-slate-800">{activePlant.lat.toFixed(5)}</span>
              </div>
              <div className="mt-3 flex items-center justify-between text-sm">
                <span className="text-slate-500">Lon</span>
                <span className="font-medium text-slate-800">{activePlant.lon.toFixed(5)}</span>
              </div>
            </div>

            <div className="space-y-2">
              <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">{t('home.map.tags')}</div>
              <div className="flex flex-wrap gap-2">
                {activePlant.power_plant_tags.length > 0 ? (
                  activePlant.power_plant_tags.map((tag) => (
                    <span
                      key={tag.id}
                      className="inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-medium"
                      style={{
                        borderColor: tag.color,
                        backgroundColor: `${tag.color}14`,
                        color: tag.color,
                      }}
                    >
                      {tag.label}
                    </span>
                  ))
                ) : (
                  <span className="text-sm text-slate-400">{t('home.powerPlantTagFilterEmpty')}</span>
                )}
              </div>
            </div>

            <button
              type="button"
              onClick={() => onPlantSelect?.(activePlant.power_plant_uuid_id)}
              className="w-full rounded-2xl bg-slate-900 px-4 py-3 text-sm font-semibold text-white transition hover:bg-slate-800"
            >
              {t('home.map.openPlant')}
            </button>
          </div>
        ) : (
          <div className="flex h-full min-h-[240px] items-center justify-center rounded-2xl border border-dashed border-slate-200 text-sm text-slate-400">
            {t('home.map.empty')}
          </div>
        )}
      </aside>
    </div>
  );
}
